/**
 * The confirmation modal, called when the accept button is pressed in either
 * the purchase order list row, or the purchase order detail view
 */
import { startCase } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Paper, ClickAwayListener, DialogContent, Modal } from '@mui/material';
import styles from './ConfirmationModal.module.css';
import Button from '../../Button/Button';
import TextInput from '../../TextInput/TextInput';

class ConfirmationModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      message: '',
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  /**
   * When accept is clicked in the modal, call submitFromModal, pass into the
   * component as a prop to send the accept request to the api
   */
  handleSubmit() {
    const { withMessage, onConfirm } = this.props;
    const { message } = this.state;
    if (withMessage) {
      onConfirm(message);
    } else {
      onConfirm();
    }
  }

  /**
   * Calls the onClose function to close the modal when clicking cancel or
   * outside of thee modal
   */
  handleClose() {
    const { onClose } = this.props;
    onClose();
  }

  /**
   * Updates state when user types in a rejection reason for the PO
   */
  handleChange({ target: { value } }) {
    this.setState({ message: value });
  }

  /**
   * The clickaway listeneter wrapping the modal handles the modal being closed
   * when clicking away from the focus of the modal. Useful when using custom
   * css for the backdrop etc
   */
  render() {
    const {
      loading,
      open,
      onClose,
      confirmButtonText,
      withMessage,
      messageTitle,
      title,
      description,
      modalId,
    } = this.props;
    const { message } = this.state;

    return (
      <Modal open={open} onClose={this.toggleRejectModal} id={modalId}>
        <DialogContent className={styles.modalContainer}>
          <ClickAwayListener onClickAway={onClose}>
            <div id="confirmation-modal" className={styles.modalInner}>
              <Paper className={styles.modal}>
                {title && <h2 className={styles.title}>{title}</h2>}
                {description && (
                  <p className={styles.description}>{description}</p>
                )}
                {withMessage && (
                  <TextInput
                    id="message-modal"
                    name="message-modal"
                    label={messageTitle}
                    value={message}
                    onChange={this.handleChange}
                    fullWidth
                  />
                )}
                <div>
                  <Button
                    type="submit"
                    id="confirmation-modal-submit"
                    variant="outlined"
                    color="primary"
                    small
                    onClick={this.handleSubmit}
                    disabled={withMessage && message < 1}
                  >
                    {startCase(
                      loading && open ? '...Loading' : confirmButtonText,
                    )}
                  </Button>
                  <Button
                    type="submit"
                    id="confirmation-modal-cancel"
                    variant="outlined"
                    color="secondary"
                    small
                    onClick={this.handleClose}
                  >
                    Cancel
                  </Button>
                </div>
              </Paper>
            </div>
          </ClickAwayListener>
        </DialogContent>
      </Modal>
    );
  }
}

ConfirmationModal.propTypes = {
  loading: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmButtonText: PropTypes.string,
  withMessage: PropTypes.bool,
  messageTitle: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  modalId: PropTypes.string,
};

ConfirmationModal.defaultProps = {
  confirmButtonText: 'Confirm',
  withMessage: false,
  messageTitle: 'Please add a confirmation message',
  description: null,
  title: null,
  modalId: '',
};

export default ConfirmationModal;
